import BaseService from "./BaseService";

// The details sections of a tenant user and their API routes.
const USER_DETAIL_ROUTES = {
  personal_details: "personal-details",
  official_details: "official-details",
  role_details: "role-details",
  dynamic_details: "dynamic-details",
  user_type: "user-type",
};

class UserService {
  static async list({ query, filters }) {
    const params = BaseService.cleanQueryParams(query);
    const queryParams = new URLSearchParams(params).toString();
    return await BaseService.callAPI(`/users/list?${queryParams}`, "POST", {
      filters,
    });
  }

  static async export({ fileType, query, filters }) {
    const params = BaseService.cleanQueryParams(query);
    const queryParams = new URLSearchParams(params).toString();
    return await BaseService.callAPI(
      `/users/export/${fileType}?${queryParams}`,
      "POST",
      {
        filters,
      },
      { responseType: "blob" }
    );
  }

  // The admin user list saga passes the list query itself, not { query }.
  static async adminUserlist(query) {
    const params = BaseService.cleanQueryParams(query);
    const queryParams = new URLSearchParams(params).toString();
    return await BaseService.callAPI(`/users?${queryParams}`);
  }

  static async managers() {
    return await BaseService.callAPI(`/users/managers`);
  }

  static async departments() {
    return await BaseService.callAPI(`/users/departments`);
  }

  static async designations() {
    return await BaseService.callAPI(`/users/designations`);
  }

  static async create(data) {
    return await BaseService.callAPI(`/users`, "POST", data);
  }

  static async bulkCreate(data) {
    return await BaseService.callAPI(`/users`, "POST", data);
  }

  static async delete({ id }) {
    return await BaseService.callAPI(`/users/${id}`, "DELETE");
  }

  static async activate({ id }) {
    return await BaseService.callAPI(`/users/${id}/activate`, "PUT");
  }

  static async deactivate({ id }) {
    return await BaseService.callAPI(`/users/${id}/deactivate`, "PUT");
  }

  static async details({ id }) {
    return await BaseService.callAPI(`/users/${id}`);
  }

  static async summary() {
    return await BaseService.callAPI(`/users/summary`);
  }

  // A tenant user's details card (Administration > Users > user): the API
  // updates each section on its own route, and has no PUT /users/{id}.
  static async update({ id, detailType, ...data }) {
    const section = USER_DETAIL_ROUTES[detailType];
    if (!section) {
      throw new Error(`Unknown user details section: ${detailType}`);
    }
    return await BaseService.callAPI(`/users/${id}/${section}`, "PUT", data);
  }

  // Clears the user's authenticator: they set it up again at their next sign-in.
  static async resetMfa({ id }) {
    return await BaseService.callAPI(`/users/${id}/reset-mfa`, "POST", {});
  }

  // Sends the welcome email again to a user who has not signed in yet (F1).
  static async resendWelcomeEmail({ id }) {
    return await BaseService.callAPI(`/users/${id}/welcome-email`, "POST", {});
  }

  // A platform user (super admin Users screen), updated as a whole: BaseService
  // adds /admin on the super admin site, so this is PUT /api/admin/users/{id}.
  static async updatePlatformUser({ id, ...data }) {
    return await BaseService.callAPI(`/users/${id}`, "PUT", data);
  }

  static async downloadTemplate() {
    return await BaseService.callAPI(
      `/users/bulk/template/download`,
      "GET",
      null,
      { responseType: "blob" }
    );
  }

  static async pendingAssignments({ id }) {
    return await BaseService.callAPI(`/users/assignment/pending/${id}`);
  }

  // completionDate: the day picked in the Mark As Completed dialog
  // ("YYYY-MM-DD"); without it the API completes the item now.
  static async markUserAssignmentAsCompleted({ userId, id, completionDate }) {
    return await BaseService.callAPI(
      `/users/assignment/mark/${userId}/completed/${id}`,
      "POST",
      completionDate ? { completionDate } : null
    );
  }
}

export default UserService;
